/* eslint-disable react/react-in-jsx-scope */
import { File, Folder, Tree } from "@/components/extension/tree-view-api";
import { Skeleton } from "@/components/ui/skeleton";
import { projectStatus } from "@/constants/constants";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";
import { useProjectStore, useTabDashboard } from "@/dashboard/store/store";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { ProgramType } from "@/types/dashboard";
import { Check, ChevronRight, FolderIcon } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

const asArray = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.["hydra:member"])) return value["hydra:member"];
  if (Array.isArray(value?.member)) return value.member;
  if (Array.isArray(value?.items)) return value.items;
  if (!value || typeof value !== "object") return [];

  return Object.values(value).filter(
    (item: any) => item && typeof item === "object" && ("id" in item || "name" in item),
  );
};

const normalizePrograms = (value: any): any[] =>
  asArray(value).map((program: any) => ({
    ...program,
    projects: asArray(program?.projects),
  }));

const TreeView = () => {
  const { project } = useProjectStore();
  const { id } = useParams<{ id: string }>();

  const { t } = useTranslation("dashboard", {
    keyPrefix: "tree",
  });

  const { tab, setTab } = useTabDashboard();
  const [searchParams] = useSearchParams();
  const currentProjectId = useMemo(() => {
    const fromRoute = Number(id);
    if (Number.isFinite(fromRoute) && fromRoute > 0) return fromRoute;
    const fromQuery = Number(searchParams.get("projectId"));
    if (Number.isFinite(fromQuery) && fromQuery > 0) return fromQuery;
    return null;
  }, [id, searchParams]);
  const stepParams = searchParams.get("step");
  const currentStep = stepParams ? parseInt(stepParams) : 1;
  const [currentStepUrl, setCurrentStepUrl] = useState(
    stepParams ? parseInt(stepParams) : 1,
  );
  const navigate = useNavigate();
  const page = window.location.pathname;
  const currentPage = page.includes("programs")
    ? "programs"
    : page.includes("project")
      ? "project"
      : page.includes("program")
        ? "program"
        : "others";
  useEffect(() => {
    const newStep = searchParams.get("step")
      ? parseInt(searchParams.get("step"))
      : 1;
    if (newStep !== currentStepUrl && project?.status === 3) {
      setCurrentStepUrl(newStep);
      const element =
        document.querySelector(`[data-step-anchor="${newStep}"]`) ??
        document.getElementById(`step${newStep}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [searchParams, project?.status]);
  const [programs, setPrograms] = useState<any[]>([]);

  const { data, loading, error } = useFetchDataV2<ProgramType[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    {
      cacheId: dashboardQueryKeys.programsInfo,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      browserCache: true,
      browserCacheKey: dashboardQueryKeys.programsInfo,
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      refetchOnWindowFocus: true,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.programsInfo,
    },
  );

  useEffect(() => {
    setPrograms(normalizePrograms(data));
  }, [data]);

  const handleFilter = (filterStatus, filterFinancements) => {
    const basePrograms = normalizePrograms(data);
    if (filterStatus.length === 0 && filterFinancements.length === 0) {
      setPrograms(basePrograms);
      return;
    }
    const newPrograms = [];
    for (let i = 0; i < basePrograms.length; i++) {
      const program = { ...basePrograms[i] };
      const newProjects = [];
      const programProjects = asArray(program.projects);
      for (let j = 0; j < programProjects.length; j++) {
        const project = { ...programProjects[j] };
        if (filterStatus.length === 0) {
          newProjects.push(project);
        } else {
          if (
            filterStatus.includes("Non commencé") &&
            (project.status === 0 || project.status === null)
          ) {
            newProjects.push(project);
          }
          if (filterStatus.includes("En cours") && project.status === 1) {
            newProjects.push(project);
          }
          if (
            filterStatus.includes("En attente de validation") &&
            project.status === 2
          ) {
            newProjects.push(project);
          }
          if (filterStatus.includes("Validé") && project.status === 3) {
            newProjects.push(project);
          }
          if (filterStatus.includes("Rejeté") && project.status === 4) {
            newProjects.push(project);
          }
          if (filterStatus.includes("Clôturé") && project.status === 5) {
            newProjects.push(project);
          }
        }
      }
      const newProjects2 = [];
      for (let j = 0; j < newProjects.length; j++) {
        const project = { ...newProjects[j] };
        if (filterFinancements.length === 0) {
          newProjects2.push(project);
        } else {
          let has = false;
          if (filterFinancements.includes("Pas encore financé")) {
            if (
              asArray(project.obtainedFunding).length === 0 &&
              (asArray(project.desiredFunding).length === 0 ||
                (asArray(project.desiredFunding).length > 0 && project.soldStatus !== 2))
            ) {
              newProjects2.push(project);
              has = true;
            }
          }
          if (!has && filterFinancements.includes("Partiellement financé")) {
            if (asArray(project.desiredFunding).length > 0 && project.soldStatus !== 2) {
              newProjects2.push(project);
              has = true;
            }
          }
          if (!has && filterFinancements.includes("Entierement financé")) {
            if (
              (asArray(project.desiredFunding).length > 0 && project.soldStatus === 2) ||
              asArray(project.desiredFunding).length === 0
            ) {
              newProjects2.push(project);
              has = true;
            }
          }
        }
      }
      program.projects = newProjects2;
      if (newProjects.length > 0) {
        newPrograms.push(program);
      }
    }

    setPrograms(newPrograms);
  };

  const isPersonalizedProject = (p: any) => {
    const readFlag = (source: any) =>
      Boolean(
        source?.isPersonnalized
        ?? source?.personnalized
        ?? source?.is_personnalized
        ?? false,
      );

    if (p?.id === project?.id) {
      return readFlag(project as any);
    }
    return readFlag(p);
  };

  const hasOtherInformationsStep = (p: any) => {
    const sourceProject = p?.id === project?.id ? project : p;
    const fields = sourceProject?.type?.fields;
    if (!Array.isArray(fields)) return false;
    return fields.some(
      (type: any) => type?.name === "protectedSpecies" || type?.name === "plantedSpecies",
    );
  };

  const getDynamicSidebarSteps = (p: any) => {
    const rawSteps = Array.isArray(p?.dynamicSidebarSteps) ? p.dynamicSidebarSteps : [];
    const sourceProject = p?.id === project?.id ? project : p;
    const numericStatus = Number(sourceProject?.status);
    const shouldUseIntroductionLabel = Number.isFinite(numericStatus) && numericStatus <= 1;

    return [...rawSteps]
      .sort((left: any, right: any) => {
        const stepDiff = Number(left?.step ?? 0) - Number(right?.step ?? 0);
        if (stepDiff !== 0) return stepDiff;
        return Number(left?.id ?? 0) - Number(right?.id ?? 0);
      })
      .map((step: any, index: number) => ({
        id: step?.id ?? `dynamic-${index + 1}`,
        step: Number(step?.step ?? index + 1),
        title:
          String(step?.label ?? step?.name ?? "").trim().toLowerCase() === "introduction"
            ? (shouldUseIntroductionLabel ? "Introduction" : "D?tails du projet")
            : String(step?.label ?? step?.name ?? `Etape ${index + 1}`),
      }));
  };

  const getStepDisabledClass = (p: any, step: number) => {
    const sourceProject = p?.id === project?.id ? project : p;
    if (isPersonalizedProject(p)) return "";
    if (sourceProject?.status == 3) return "";
    return sourceProject?.step == null || sourceProject?.step < step
      ? "pointer-events-none opacity-50"
      : "";
  };

  const getProjectNavigationLink = (
    p: any,
    programId: number,
    step?: number,
    tab: string = "descriptif",
  ) => {
    const sourceProject = p?.id === project?.id ? project : p;
    const status = Number(sourceProject?.status);
    const personalized = isPersonalizedProject(p);
    const shouldOpenDynamicForm =
      personalized &&
      Number.isFinite(status) &&
      status !== projectStatus.Validated;

    if (shouldOpenDynamicForm) {
      const params = new URLSearchParams();
      params.set("programId", String(programId));
      params.set("projectId", String(p?.id));
      if (step !== undefined) params.set("step", String(step));
      if (tab) params.set("tab", tab);
      return `/dashboard/project/create-custom?${params.toString()}`;
    }

    const params = new URLSearchParams();
    if (step !== undefined) params.set("step", String(step));
    if (tab) params.set("tab", tab);
    const query = params.toString();
    return `/dashboard/project/${p?.id}${query ? `?${query}` : ""}`;
  };

  const elements = [
    {
      id: "1",
      isSelectable: true,
      name: "Mes programmes",
      children: programs
        ?.map((item: ProgramType, key: number) => {
          const itemProjects = asArray(item?.projects);
          return {
            id: `program-${item.id}`,
            isSelectable: true,
            name: item.name + "-" + item.id,
            children: itemProjects
              .map((project: { name: string; id: number }) => {
                return {
                  id: `project-${project.id}`,
                  isSelectable: true,
                  name: project.name + "-" + project.id,
                  children: [
                    {
                      id: `project-${project.id}`,
                      isSelectable: true,
                      name: "Descriptif",
                    },
                    {
                      id: `project-${project.id}-step-1`,
                      isSelectable: true,
                      name: "Détails du projet",
                    },
                    {
                      id: `project-${project.id}-step-2`,
                      isSelectable: true,
                      name: "Objectifs du projet",
                    },
                    {
                      id: `project-${project.id}-step-3`,
                      isSelectable: true,
                      name: "Financements",
                    },
                    {
                      id: `project-${project.id}-step-4`,
                      isSelectable: true,
                      name: "Autres informations",
                    },
                    {
                      id: `project-${project.id}-step-5`,
                      isSelectable: true,
                      name: "Parcelle et localisation",
                    },
                    {
                      id: `project-${project.id}-step-6`,
                      isSelectable: true,
                      name: "Pièces jointes",
                    },
                  ],
                };
              })
              .filter(Boolean),
          };
        })
        .filter(Boolean),
    },
  ];

  // Calculer l'ID initial sélectionné
  const initialSelectedId =
    currentPage === "program"
      ? `program-${id}`
      : currentPage === "programs"
        ? `1`
        : currentPage === "project"
          ? (currentProjectId ? `project-${currentProjectId}` : "999")
          : "999";
  // Gestion du chargement et des erreurs
  if (loading && programs.length === 0) return <TreeSkeleton />;
  if (error && programs.length === 0)
    return (
      <Tree
        elements={[{ id: "1", isSelectable: true, name: "Mes programmes" }]}
        className="mb-4"
        id="mes-programmes"
      >
        <Folder element={t("Mes programmes")} value="1" visibleChevron={true}>
          <span className="ml-3 text-xs text-muted-foreground">
            {t("Une erreur est survenue:")} {error}
          </span>
        </Folder>
      </Tree>
    );

  return (
    <>
      <Tree
        initialSelectedId={initialSelectedId}
        elements={elements}
        className="mb-4 "
        id="mes-programmes"
      >
        <Folder
          element={t("Mes programmes")}
          value="1"
          visibleChevron={true}
          onFilterChange={(value, financements) => {
            // console.log('mandeeee...', financements)
            handleFilter(value, financements);
          }}
        >
          <div className="flex flex-col gap-1">
            {programs?.map((item: ProgramType, key: number) => {
              const itemProjects = asArray(item?.projects);

              return (
                <Folder
                  element={item.name}
                  value={`program-${item.id}`}
                  key={key}
                  visibleChevron={true}
                  projectId={item.id}
                  className={`${
                    item.id.toString() == id ? "bg-primary/10" : ""
                  } py-1`}
                  isSold={itemProjects.some(
                    (p: any) => p.soldStatus === 2 && p.notifSold !== true,
                  )}
                  type={"program"}
                >
                  {itemProjects.map((p: any, k: number) => (
                  <Folder
                    key={p.id}
                    value={`project-${p.id}`}
                    element={p.name}
                    className={`w-full items-start text-left `}
                    projectId={p.id}
                    visibleChevron={true}
                    isSold={p.soldStatus === 2 && p.notifSold !== true}
                    type={"project"}
                    status={p.status}
                    isPersonnalized={isPersonalizedProject(p)}
                    programContextId={item.id}
                  >
                    {(() => {
                      const dynamicSidebarSteps = getDynamicSidebarSteps(p);
                      const shouldUseDynamicSidebar =
                        isPersonalizedProject(p) && dynamicSidebarSteps.length > 0;

                      return (
                        <>
                    {/* Fiche Synthetic */}
                    <File
                      id={`project-${p.id}-step-8`}
                      value="synthetic"
                      className={`${
                        p.status !== projectStatus.Validated
                          ? "pointer-events-none opacity-25"
                          : ""
                      }`}
                      fileIcon=""
                    >
                      <StepMenuTab
                        title={t("Fiche Synthétique")}
                        link={getProjectNavigationLink(p, item.id, undefined, "synthetic")}
                        currentTab={tab}
                        tab="synthetic"
                        setTab={setTab}
                        currentProject={parseInt(id)}
                        project={p.id}
                      />
                    </File>
                    {/* Descriptif du projet */}
                    <File
                      id={`project-${p.id}-step-1`}
                      value={`project-${p.id}-step-1`}
                      fileIcon=""
                      className="-mt-2 w-full"
                    >
                      <StepMenuTab
                        title={t("Descriptif")}
                        link={getProjectNavigationLink(p, item.id, undefined, "descriptif")}
                        currentTab={tab}
                        tab="descriptif"
                        setTab={setTab}
                        currentProject={parseInt(id)}
                        project={p.id}
                      />
                    </File>

                    {shouldUseDynamicSidebar ? (
                      dynamicSidebarSteps.map((dynamicStep, dynamicIndex) => (
                        <File
                          key={`project-${p.id}-dynamic-step-${dynamicStep.step}`}
                          id={`project-${p.id}-dynamic-step-${dynamicStep.step}`}
                          value={`project-${p.id}-dynamic-step-${dynamicStep.step}`}
                          fileIcon=""
                          className={getStepDisabledClass(p, dynamicStep.step)}
                        >
                          <StepMenu
                            status={project?.status}
                            title={dynamicStep.title}
                            onClick={() => {
                              currentProjectId == p.id
                                ? navigate(getProjectNavigationLink(p, item.id, dynamicStep.step, "descriptif"))
                                : (window.location.href = getProjectNavigationLink(p, item.id, dynamicStep.step, "descriptif"));
                              setTab("descriptif");
                            }}
                            currentStep={currentStep}
                            step={dynamicStep.step}
                            state={
                              p.id === project?.id
                                ? project?.step >= dynamicStep.step
                                : p.step >= dynamicStep.step
                            }
                            stepLabel={String(dynamicIndex + 1)}
                            currentProject={currentProjectId ?? -1}
                            project={p.id}
                            currentTab={tab}
                          />
                        </File>
                      ))
                    ) : (
                      <>
                    <File
                      id={`project-${p.id}-step-1-1`}
                      value={`project-${p.id}-step-1`}
                      fileIcon=""
                    >
                      <StepMenu
                        status={project?.status}
                        title={t("Détails du projet")}
                        onClick={() => {
                          currentProjectId == p.id
                            ? navigate(getProjectNavigationLink(p, item.id, 1, "descriptif"))
                            : (window.location.href = getProjectNavigationLink(p, item.id, 1, "descriptif"));

                          setTab("descriptif");
                        }}
                        currentStep={currentStep}
                        step={1}
                        state={
                          p.id === project?.id
                            ? project?.step >= 1
                            : p.step >= 1
                        }
                        stepLabel="1"
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                        currentTab={tab}
                      />
                    </File>
                    {!isPersonalizedProject(p) && (
                      <File
                        id={`project-${p.id}-step-2`}
                        value={`project-${p.id}-step-2`}
                        fileIcon=""
                        className={getStepDisabledClass(p, 2)}
                      >
                        <StepMenu
                          status={project?.status}
                          title={`${t("Objectifs du projet")}`}
                          onClick={() => {
                            currentProjectId == p.id
                              ? navigate(getProjectNavigationLink(p, item.id, 2, "descriptif"))
                              : (window.location.href = getProjectNavigationLink(p, item.id, 2, "descriptif"));
                            setTab("descriptif");
                          }}
                          currentStep={currentStep}
                          step={2}
                          state={
                            p.id === project?.id
                              ? project?.step >= 2
                              : p.step >= 2
                          }
                          stepLabel="2"
                          currentProject={currentProjectId ?? -1}
                          project={p.id}
                          currentTab={tab}
                        />
                      </File>
                    )}
                    <File
                      id={`project-${p.id}-step-3`}
                      value={`project-${p.id}-step-3`}
                      fileIcon=""
                      className={getStepDisabledClass(p, 3)}
                    >
                      <StepMenu
                        status={project?.status}
                        title={t("Financements")}
                        onClick={() => {
                          currentProjectId == p.id
                            ? navigate(getProjectNavigationLink(p, item.id, 3, "descriptif"))
                            : (window.location.href = getProjectNavigationLink(p, item.id, 3, "descriptif"));

                          setTab("descriptif");
                        }}
                        currentStep={currentStep}
                        step={3}
                        state={
                          p.id === project?.id
                            ? project?.step >= 3
                            : p.step >= 3
                        }
                        stepLabel="3"
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                        currentTab={tab}
                      />
                    </File>
                    {!isPersonalizedProject(p) && (
                      p.id === project?.id
                      ? hasOtherInformationsStep(p) && (
                          <File
                            id={`project-${p.id}-step-4`}
                            value={`project-${project?.id}-step-4`}
                            fileIcon=""
                            className={getStepDisabledClass(p, 4)}
                          >
                            <StepMenu
                              status={project?.status}
                              title={t("Autres informations")}
                              onClick={() => {
                                currentProjectId == p.id
                                  ? navigate(getProjectNavigationLink(p, item.id, 4, "descriptif"))
                                  : (window.location.href = getProjectNavigationLink(p, item.id, 4, "descriptif"));
                                setTab("descriptif");
                              }}
                              currentStep={currentStep}
                              step={4}
                              state={project?.step >= 4}
                              stepLabel="4"
                              currentProject={currentProjectId ?? -1}
                              project={p.id}
                              currentTab={tab}
                            />
                          </File>
                        )
                      : hasOtherInformationsStep(p) && (
                          <File
                            id={`project-${p.id}-step-4`}
                            value={`project-${p.id}-step-4`}
                            fileIcon=""
                            className={getStepDisabledClass(p, 4)}
                          >
                            <StepMenu
                              status={project?.status}
                              title={t("Autres informations")}
                              onClick={() => {
                                currentProjectId == p.id
                                  ? navigate(getProjectNavigationLink(p, item.id, 4, "descriptif"))
                                  : (window.location.href = getProjectNavigationLink(p, item.id, 4, "descriptif"));
                                setTab("descriptif");
                              }}
                              currentStep={currentStep}
                              step={4}
                              state={
                                p.id === project?.id
                                  ? project?.step >= 4
                                  : p.step >= 4
                              }
                              stepLabel="4"
                              currentProject={currentProjectId ?? -1}
                              project={p.id}
                              currentTab={tab}
                            />
                          </File>
                        )
                    )}
                    <File
                      id={`project-${p.id}-step-5`}
                      value={`project-${p.id}-step-5`}
                      fileIcon=""
                      className={getStepDisabledClass(p, 5)}
                    >
                      <StepMenu
                        status={project?.status}
                        title={t("Parcelle et localisation")}
                        onClick={() => {
                          currentProjectId == p.id
                            ? navigate(getProjectNavigationLink(p, item.id, 5, "descriptif"))
                            : (window.location.href = getProjectNavigationLink(p, item.id, 5, "descriptif"));
                          setTab("descriptif");
                        }}
                        currentStep={currentStep}
                        step={5}
                        state={
                          p.id === project?.id
                            ? project?.step >= 5
                            : p.step >= 5
                        }
                        stepLabel={
                          hasOtherInformationsStep(p)
                            ? "5"
                            : "4"
                        }
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                        currentTab={tab}
                      />
                    </File>
                    <File
                      id={`project-${p.id}-step-6`}
                      value={`project-${p.id}-step-6`}
                      fileIcon=""
                      className={getStepDisabledClass(p, 6)}
                    >
                      <StepMenu
                        status={project?.status}
                        title={t("Pièces jointes")}
                        onClick={() => {
                          currentProjectId == p.id
                            ? navigate(getProjectNavigationLink(p, item.id, 6, "descriptif"))
                            : (window.location.href = getProjectNavigationLink(p, item.id, 6, "descriptif"));
                          setTab("descriptif");
                        }}
                        currentStep={currentStep}
                        step={6}
                        state={
                          p.id === project?.id
                            ? project?.step >= 6
                            : p.step >= 6
                        }
                        stepLabel={
                          hasOtherInformationsStep(p)
                            ? "6"
                            : "5"
                        }
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                        currentTab={tab}
                      />
                    </File>
                      </>
                    )}
                    {/* Suivi de perfo */}
                    <File
                      id={`project-${p.id}-step-7`}
                      value="suivi"
                      fileIcon=""
                      // className={`${
                      //   p.status !== projectStatus.Validated
                      //     ? "pointer-events-none opacity-25"
                      //     : ""
                      // }`}
                    >
                      <StepMenuTab
                        title={t("Suivi des indicateurs")}
                        link={getProjectNavigationLink(p, item.id, undefined, "suivi-perf")}
                        currentTab={tab}
                        tab="suivi-perf"
                        setTab={setTab}
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                      />
                    </File>
                    {/* Reporting */}
                    <File
                      id={`project-${p.id}-step-8`}
                      value="reporting"
                      fileIcon=""
                      className={`${
                        p.status !== projectStatus.Validated
                          ? "pointer-events-none opacity-25"
                          : ""
                      }`}
                    >
                      <StepMenuTab
                        title={t("Reporting")}
                        link={getProjectNavigationLink(p, item.id, undefined, "reporting")}
                        currentTab={tab}
                        tab="reporting"
                        setTab={setTab}
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                      />
                    </File>
                    {/* Related Document*/}
                    <File
                      id={`project-${p.id}-step-8`}
                      value="document"
                      // className={`${
                      //   p.status !== projectStatus.Validated
                      //     ? "pointer-events-none opacity-25"
                      //     : ""
                      // }`}
                      fileIcon=""
                    >
                      <StepMenuTab
                        title={t("Documents liés")}
                        link={getProjectNavigationLink(p, item.id, undefined, "document")}
                        currentTab={tab}
                        tab="document"
                        setTab={setTab}
                        currentProject={currentProjectId ?? -1}
                        project={p.id}
                      />
                    </File>
                        </>
                      );
                    })()}
                  </Folder>
                  ))}
                </Folder>
              );
            })}
          </div>
        </Folder>
      </Tree>
    </>
  );
};

const StepMenu: FC<
  {
    title: string;
    currentStep: number;
    step: number;
    status: number;
    state: boolean;
    stepLabel?: string;
    currentProject: number;
    project: number | string;
    currentTab: string;
  } & React.HTMLAttributes<HTMLDivElement>
> = ({
  title,
  currentStep,
  step,
  status,
  state,
  stepLabel,
  currentProject,
  project,
  currentTab,
  ...props
}) => {
  return (
    <div className={`relative ml-5`} {...props}>
      <div className={`flex items-start rounded-md py-1 pl-0 text-left`}>
        <p className="mb-0 flex items-center text-[13px]">
          {state || status == 3 || status == 2 ? (
            <span
              className={`mr-2 flex size-6 items-center justify-center ${
                currentStep == step &&
                currentProject == project &&
                currentTab == "descriptif"
                  ? "bg-primary text-background"
                  : "bg-primary/15"
              } rounded-full`}
            >
              <Check size={15} />{" "}
            </span>
          ) : (
            stepLabel && (
              <span
                className={`mr-2 flex size-6 items-center justify-center text-[13px] ${
                  currentStep == step &&
                  currentProject == project &&
                  currentTab == "descriptif"
                    ? "bg-primary text-background"
                    : "bg-primary/15 text-foreground"
                }  rounded-full`}
              >
                {stepLabel}
              </span>
            )
          )}
          <span
            className={`flex-1 text-left text-[13px] ${
              currentProject == project &&
              currentStep >= step &&
              currentTab == "descriptif"
                ? "font-semibold"
                : ""
            } ${
              !stepLabel
                ? "rounded-sm bg-primary/15 px-2 py-1 font-semibold"
                : ""
            } line-clamp-2`}
          >
            {title}
          </span>
        </p>
      </div>
    </div>
  );
};

const StepMenuTab: FC<
  {
    title: string;
    link: string;
    currentTab: string;
    tab: "synthetic" | "descriptif" | "reporting" | "suivi-perf";
    setTab: (
      tab: "synthetic" | "descriptif" | "reporting" | "suivi-perf",
    ) => void;
    currentProject: number;
    project: number | string;
  } & React.HTMLAttributes<HTMLDivElement>
> = ({
  title,
  link,
  currentTab,
  tab,
  setTab,
  currentProject,
  project,
  ...props
}) => {
  return (
    <div className={`relative ml-3 w-full`} {...props}>
      <Link
        to={link}
        className={`relative flex w-full items-start rounded-md py-1 pl-0 text-left`}
        onClick={() => setTab(tab)}
      >
        {currentTab == tab && (
          <ChevronRight className="absolute right-full top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
        )}
        <p className="mb-0 flex w-full items-center text-[13px]">
          <span
            className={`line-clamp-2 w-full rounded-sm ${
              currentTab == tab && currentProject == project
                ? "bg-primary/15 font-semibold text-primary"
                : "text-muted-foreground"
            } px-2 py-1 text-left text-[13px] `}
          >
            {title}
          </span>
        </p>
      </Link>
    </div>
  );
};

export default TreeView;

export const TreeSkeleton = () => {
  return (
    <Tree
      elements={[{ id: "1", isSelectable: true, name: "Mes programmes" }]}
      className="mb-4"
      id="mes-programmes"
    >
      <Folder element="Mes programmes" value="1" visibleChevron={true}>
        {Array.from({ length: 10 }).map((_, i) => (
          <File
            id={`project-Skeleton-1`}
            value={`project-Skeleton-${i}`}
            fileIcon=""
            className="mt-2 w-full"
            key={i}
          >
            <ChevronRight
              size={20}
              className={`mr-1 text-[#878787] ${i % 2 == 1 && " ml-6"}`}
            />
            {i % 2 == 0 && (
              <FolderIcon className="flex size-5 text-[#878787]" />
            )}
            <Skeleton className="rounded-[20px] w-[100px] h-3 bg-[#bdbebf]" />
          </File>
        ))}
      </Folder>
    </Tree>
  );
};
